// services/creditService.ts
// 积分系统服务 - 充值、扣费、查询
//
// credits_ledger.user_id 与后台 admin lookup 一致：对应 public.users.id（勿用 auth.users.id 查账本）

import { supabase } from '../src/lib/supabaseClient';
import { api } from './apiClient';

function normalizeEmailForCredits(email: string): string {
  return (email || '').trim().toLowerCase();
}

/**
 * 按邮箱解析 public.users.id，与 admin GET /users/lookup 一致（先 eq 再 ilike）。
 * 用于查询 credits_ledger；勿传入 auth.users.id。
 */
export async function getPublicUserIdForLedger(userEmail: string): Promise<string | null> {
  const email = normalizeEmailForCredits(userEmail);
  if (!email) return null;
  try {
    const r1 = await supabase
      .schema('public')
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    if (r1.data?.id) return String(r1.data.id);

    const r2 = await supabase
      .schema('public')
      .from('users')
      .select('id')
      .ilike('email', email)
      .maybeSingle();
    if (r2.data?.id) return String(r2.data.id);

    if (r1.error || r2.error) {
      console.warn('[Credits] public.users 无该邮箱或查询失败:', email, r1.error, r2.error);
    }
    return null;
  } catch (e: any) {
    console.warn('[Credits] getPublicUserIdForLedger 异常:', e?.message || e);
    return null;
  }
}

// ============================================
// 1. 套餐配置（与SQL中的数据一致）
// ============================================

export type PlanType = 'flexible' | 'entry' | 'pro' | 'flagship' | 'yearly';

export interface CreditPlan {
  price: number;           // 支付金额（元）
  totalCredits: number;    // 总积分（付费+赠送）
  paidCredits: number;     // 付费部分
  bonusCredits: number;    // 赠送部分
  expiresDays: number;     // 过期天数
  displayName: string;     // 展示名称
  description: string;     // 描述
}

export const CREDIT_PLANS: Record<PlanType, CreditPlan> = {
  flexible: {
    price: 15,
    totalCredits: 150,
    paidCredits: 150,
    bonusCredits: 0,
    expiresDays: 30,
    displayName: '灵活充值',
    description: '150积分，30天有效'
  },
  entry: {
    price: 60,
    totalCredits: 600,
    paidCredits: 600,
    bonusCredits: 0,
    expiresDays: 30,
    displayName: '入门版',
    description: '600积分，30天有效'
  },
  pro: {
    price: 150,
    totalCredits: 1800,
    paidCredits: 1500,
    bonusCredits: 300,  // 1500+300=1800
    expiresDays: 30,
    displayName: '专业版',
    description: '1800积分（含赠送300），30天有效'
  },
  flagship: {
    price: 300,
    totalCredits: 6000,
    paidCredits: 3000,
    bonusCredits: 3000,  // 3000+3000=6000
    expiresDays: 60,
    displayName: '旗舰版',
    description: '6000积分（含赠送3000），60天有效'
  },
  yearly: {
    price: 1680,
    totalCredits: 36000,
    paidCredits: 36000,
    bonusCredits: 0,
    expiresDays: 180,
    displayName: '半年度会员',
    description: '36000积分，180天有效'
  }
};

// ============================================
// 2. 类型定义
// ============================================

export type FeatureType = 
  | 'render'           // 灯光渲染
  | 'upscale'          // 高清放大
  | 'reconstruct'      // 光影重构
  | 'tone_mapping'     // 色调映射
  | 'text_reconstruct' // 文本重构
  | 'physical_reconstruct'; // 物理重构

export interface DeductResult {
  success: boolean;
  error?: string;
  deductedAmount?: number;
  remainingBalance?: number;
  transactions?: Array<{
    ledgerId: string;
    amount: number;
    expiresAt: string;
  }>;
}

export interface CreditBalance {
  total: number;
  breakdown: Array<{
    ledgerId: string;
    remaining: number;
    expiresAt: string;
    planType: string;
    daysLeft: number;
  }>;
}

export interface PurchaseHistory {
  id: string;
  planType: string;
  displayName: string;
  purchasePrice: number;
  totalCredits: number;
  paidCredits: number;
  bonusCredits: number;
  remainingCredits: number;
  expiresAt: string;
  createdAt: string;
  isExpired: boolean;
  daysLeft: number;
  orderId?: string;
  /** alipay | admin_manual 等 */
  paymentMethod?: string | null;
}

export interface UsageHistory {
  id: string;
  ledgerId: string;
  amountUsed: number;
  featureType: string;
  description: string;
  createdAt: string;
}

// ============================================
// 3. 工具函数
// ============================================

/**
 * 计算过期时间
 */
export function calculateExpiresAt(planType: PlanType): Date {
  const plan = CREDIT_PLANS[planType];
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + plan.expiresDays);
  return new Date(expiresAt.toISOString()); // 转UTC
}

/**
 * 获取套餐显示信息
 */
export function getPlanDisplayInfo(planType: string): { name: string; description: string } {
  const plan = CREDIT_PLANS[planType as PlanType];
  if (plan) {
    return { name: plan.displayName, description: plan.description };
  }
  return { name: planType, description: '' };
}

// ============================================
// 4. 核心服务函数
// ============================================

/**
 * 【充值】记录充值（带幂等性保护）
 * 防止重复充值：同一订单号只写入一次
 * 
 * @param userId 用户ID
 * @param planType 套餐类型
 * @param orderId 支付订单号（幂等性关键）
 * @param paymentMethod 支付方式：alipay/wechat
 */
export async function recordRecharge(
  userId: string,
  planType: PlanType,
  orderId: string,
  paymentMethod: string = 'alipay'
): Promise<{ success: boolean; error?: string; data?: any }> {
  try {
    // 1. 【关键】检查该订单是否已处理
    const { data: existing } = await supabase
      .from('credits_ledger')
      .select('id')
      .eq('order_id', orderId)
      .maybeSingle();
    
    if (existing) {
      console.log('[Credits] 订单已处理，跳过重复充值:', orderId);
      return { success: true }; // 幂等性：已处理直接返回成功
    }
    
    // 2. 获取套餐配置
    const plan = CREDIT_PLANS[planType];
    const expiresAt = calculateExpiresAt(planType);
    
    // 3. 插入充值记录
    const { data, error } = await supabase.from('credits_ledger').insert({
      user_id: userId,
      amount_total: plan.totalCredits,
      amount_paid: plan.paidCredits,
      amount_bonus: plan.bonusCredits,
      amount_remaining: plan.totalCredits, // 初始剩余等于总额
      purchase_price: plan.price,
      plan_type: planType,
      order_id: orderId,
      payment_method: paymentMethod,
      expires_at: expiresAt.toISOString()
    }).select();
    
    if (error) {
      // 唯一约束冲突（极端并发情况）
      if (error.code === '23505') {
        console.log('[Credits] 订单已存在（并发冲突）:', orderId);
        return { success: true };
      }
      console.error('[Credits] 充值记录写入失败:', error);
      return { success: false, error: error.message };
    }
    
    console.log('[Credits] 充值成功:', orderId, planType, plan.totalCredits, '积分');
    return { success: true, data };
    
  } catch (err: any) {
    console.error('[Credits] recordRecharge 异常:', err);
    return { success: false, error: err.message };
  }
}

/**
 * 【扣费】仅 `POST /api/credits/deduct`（JWT 由 api 层附带）。
 */
export async function deductCredits(
  featureType: FeatureType,
  description?: string
): Promise<DeductResult> {
  try {
    const raw = (await api.post('/api/credits/deduct', {
      feature_type: featureType,
      description,
    })) as {
      success?: boolean;
      error?: string;
      available?: number;
      deducted?: number;
      remaining_balance?: number;
      remainingBalance?: number;
    };

    if (!raw || raw.success !== true) {
      const avail = raw?.available ?? raw?.remaining_balance ?? raw?.remainingBalance;
      return {
        success: false,
        error: raw?.error || 'DEDUCT_FAILED',
        remainingBalance: avail != null && Number.isFinite(Number(avail)) ? Number(avail) : undefined,
      };
    }

    const rem = raw.remaining_balance ?? raw.remainingBalance;
    return {
      success: true,
      deductedAmount: raw.deducted,
      remainingBalance: rem != null && Number.isFinite(Number(rem)) ? Number(rem) : undefined,
    };
  } catch (err: any) {
    console.error('[Credits] deductCredits 异常:', err);
    return { success: false, error: err.message };
  }
}

/**
 * 【查询】获取用户当前可用积分（未过期且 amount_remaining > 0）
 * @param publicUsersId public.users.id（与 credits_ledger.user_id 外键一致；含 admin_manual 等所有 payment_method）
 */
export async function getCreditBalance(publicUsersId: string): Promise<CreditBalance> {
  try {
    const { data: rawRows, error } = await supabase
      .schema('public')
      .from('credits_ledger')
      .select('id, amount_remaining, expires_at, plan_type')
      .eq('user_id', publicUsersId)
      .gt('amount_remaining', 0)
      .order('expires_at', { ascending: true });

    if (error) {
      console.error('[Credits] 查询余额失败:', error);
      return { total: 0, breakdown: [] };
    }

    const nowMs = Date.now();
    const data = (rawRows || []).filter((row) => {
      if (!row.expires_at) return true;
      return new Date(row.expires_at).getTime() > nowMs;
    });

    const now = new Date();
    const total = data?.reduce((sum, item) => sum + item.amount_remaining, 0) || 0;
    
    return {
      total,
      breakdown: data?.map(item => {
        const expiresAt = new Date(item.expires_at);
        const daysLeft = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        
        return {
          ledgerId: item.id,
          remaining: item.amount_remaining,
          expiresAt: item.expires_at,
          planType: item.plan_type,
          daysLeft
        };
      }) || []
    };
    
  } catch (err: any) {
    console.error('[Credits] getCreditBalance 异常:', err);
    return { total: 0, breakdown: [] };
  }
}

/**
 * 【查询】获取用户充值历史（含 admin_manual）
 * @param publicUsersId public.users.id
 */
export async function fetchPurchaseHistory(publicUsersId: string): Promise<PurchaseHistory[]> {
  try {
    const { data, error } = await supabase
      .schema('public')
      .from('credits_ledger')
      .select('*')
      .eq('user_id', publicUsersId)
      .order('created_at', { ascending: false }); // 最新的在前
    
    if (error) {
      console.error('[Credits] 查询充值历史失败:', error);
      return [];
    }
    
    const now = new Date();
    
    return data?.map(item => {
      const expiresAt = new Date(item.expires_at);
      const daysLeft = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      const plan = CREDIT_PLANS[item.plan_type as PlanType];
      const isAdminManual = item.payment_method === 'admin_manual';

      return {
        id: item.id,
        planType: item.plan_type,
        displayName: isAdminManual ? '管理员代充值' : plan?.displayName || item.plan_type,
        purchasePrice: item.purchase_price,
        totalCredits: item.amount_total,
        paidCredits: item.amount_paid,
        bonusCredits: item.amount_bonus,
        remainingCredits: item.amount_remaining,
        expiresAt: item.expires_at,
        createdAt: item.created_at,
        isExpired: daysLeft === 0,
        daysLeft,
        orderId: item.order_id,
        paymentMethod: item.payment_method,
      };
    }) || [];
    
  } catch (err: any) {
    console.error('[Credits] fetchPurchaseHistory 异常:', err);
    return [];
  }
}

/**
 * 【查询】获取消费流水
 */
export async function fetchUsageHistory(userId: string, limit: number = 50): Promise<UsageHistory[]> {
  try {
    const { data, error } = await supabase
      .from('credits_usage')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('[Credits] 查询消费流水失败:', error);
      return [];
    }
    
    return data?.map(item => ({
      id: item.id,
      ledgerId: item.ledger_id,
      amountUsed: item.amount_used,
      featureType: item.feature_type,
      description: item.description,
      createdAt: item.created_at
    })) || [];
    
  } catch (err: any) {
    console.error('[Credits] fetchUsageHistory 异常:', err);
    return [];
  }
}

// ============================================
// 5. 预设积分消耗配置（同步估算 UI；精确单价走 HTTP plan-deduct）
// ============================================

export const DEFAULT_LIGHTING_RENDER_CREDITS = 15;

export const CREDIT_CONSUMPTION = {
  render: { amount: DEFAULT_LIGHTING_RENDER_CREDITS, name: '灯光渲染', description: '灯光图渲染生成' },
  upscale: { amount: 10, name: 'AI高清放大', description: '图片高清放大处理' },
  reconstruct: { amount: 10, name: '光影重构', description: '光影效果重构' },
  tone_mapping: { amount: 10, name: '色调映射', description: '色调映射调整' },
  text_reconstruct: { amount: 10, name: '文本重构', description: '文本内容重构' },
  physical_reconstruct: { amount: 10, name: '物理重构', description: '物理效果重构' },
} as const;

/**
 * 获取功能消耗积分
 */
export function getFeatureCreditCost(featureType: FeatureType): number {
  return CREDIT_CONSUMPTION[featureType]?.amount || 10;
}

/**
 * 从 `GET /api/credits/plan-deduct` 取单价；无本地数值兜底。
 */
export async function getPlanDeductCreditsForFeature(featureType: FeatureType): Promise<number> {
  const data = (await api.getCreditsPlanDeduct(featureType)) as { amount?: unknown };
  const n = typeof data.amount === 'number' ? data.amount : parseInt(String(data.amount ?? ''), 10);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`[Credits] plan-deduct 无效响应: ${JSON.stringify(data)}`);
  }
  return n;
}

export async function getLightingRenderFeeCredits(): Promise<number> {
  return getPlanDeductCreditsForFeature('render');
}

export async function getFeatureCreditCostAsync(featureType: FeatureType): Promise<number> {
  return getPlanDeductCreditsForFeature(featureType);
}

// ============================================
// 6. 快捷扣费函数（带预设消耗）
// ============================================

/**
 * 渲染扣费快捷函数
 */
export async function deductForRender(description?: string): Promise<DeductResult> {
  return deductCredits('render', description);
}

/**
 * 放大扣费快捷函数
 */
export async function deductForUpscale(description?: string): Promise<DeductResult> {
  return deductCredits('upscale', description);
}

/**
 * 重构扣费快捷函数
 */
export async function deductForReconstruct(description?: string): Promise<DeductResult> {
  return deductCredits('reconstruct', description);
}
